/**
 * Seed content for the My Teams ("My School" / "My District") admin pages.
 *
 * These constants drive the UI today while the aggregation pipeline lands behind
 * them. When the SQL migration `school_team_snapshots` / `school_team_focus_areas`
 * / `school_team_observations` is applied and populated, swap each constant for
 * data fetched per snapshot. The shapes below mirror the migration so the UI
 * does not change when real data arrives.
 *
 * IMPORTANT: This is *aggregate* school-level data. Individual sessions and
 * conversations are never represented here — only patterns that emerge across
 * 25%+ of staff (per the privacy banner copy).
 */
import type { ChatEntry } from "@/types/chat";

/** Stable accent variants the focus cards use; matches existing tailwind tokens. */
export type FocusAccent = "self_regulation" | "conversation_lab" | "restoration" | "growth";

export interface FocusArea {
  id: string;
  /**
   * Optional chat entry this card maps to. When present, "Talk it through"
   * jumps the leader into the relevant chat with context preloaded.
   */
  chatEntry?: ChatEntry;
  label: string;
  /** Short category label rendered under the title (e.g. "Self-regulation"). */
  category: string;
  accent: FocusAccent;
  percent: number;
}

export type ObservationDirection = "up" | "right" | "down";
export type ObservationToneId = "positive" | "worth_exploring" | "neutral";

export interface ObservationTag {
  label: string;
  tone: ObservationToneId;
}

export interface Observation {
  id: string;
  direction: ObservationDirection;
  body: string;
  tags: ObservationTag[];
}

export interface SchoolTeamSnapshot {
  schoolId: string;
  schoolName: string;
  /** One-line school character motto. Rendered italic under the title. */
  tagline: string;
  /** Display name for the term, e.g. "Spring 2026". */
  semesterLabel: string;
  /** Number of staff who have any session activity this semester. */
  helperCount: number;
  focusAreas: FocusArea[];
  observations: Observation[];
}

export const SAMPLE_SCHOOL_TEAM_SNAPSHOT: SchoolTeamSnapshot = {
  schoolId: "sample-lincoln-elementary",
  schoolName: "Lincoln Elementary",
  tagline: "a mirror held up to the whole building, without naming anyone inside it.",
  semesterLabel: "Spring 2026",
  helperCount: 47,
  focusAreas: [
    {
      id: "reset-nervous-system",
      chatEntry: "reset_my_nervous_system",
      label: "Reset my Nervous System",
      category: "Self-regulation",
      accent: "self_regulation",
      percent: 72,
    },
    {
      id: "parent-conversations",
      chatEntry: "strengthen_my_voice",
      label: "Parent Conversations",
      category: "Conversation Lab",
      accent: "conversation_lab",
      percent: 58,
    },
    {
      id: "reclaim-my-balance",
      chatEntry: "reclaim_my_balance",
      label: "Reclaim my Balance",
      category: "Restoration",
      accent: "restoration",
      percent: 44,
    },
    {
      id: "expand-my-impact",
      chatEntry: "expand_my_impact",
      label: "Expand my Impact",
      category: "Growth",
      accent: "growth",
      percent: 31,
    },
  ],
  observations: [
    {
      id: "returning-rates",
      direction: "up",
      body: "Your staff is returning after difficult weeks at higher rates than last semester — something is building staying power here, not just in-the-moment relief.",
      tags: [
        { label: "Resilience", tone: "positive" },
        { label: "Positive", tone: "positive" },
      ],
    },
    {
      id: "self-reg-and-parents",
      direction: "right",
      body: "Self-regulation and parent conversations are where your staff is spending the most time — more than peer or collegial support. Worth understanding whether staff feel equipped to navigate parent relationships, or whether they're absorbing that strain largely alone.",
      tags: [
        { label: "Relational", tone: "neutral" },
        { label: "Worth Exploring", tone: "worth_exploring" },
      ],
    },
    {
      id: "scenario-practice",
      direction: "up",
      body: "Scenario practice in the Conversation Lab is up this semester. Staff are actively rehearsing difficult conversations — not only processing feelings after the fact.",
      tags: [
        { label: "Practice", tone: "positive" },
        { label: "Positive", tone: "positive" },
      ],
    },
  ],
};

/**
 * Example snapshot for the My Teams preview panel — shows leaders what the page
 * looks like once enough staff usage crosses the 25% privacy threshold.
 * Never mixed with live aggregate data; always labeled "Example preview" in UI.
 */
export function buildTeamInsightsPreviewSnapshot(
  schoolName: string,
  schoolId: string,
): SchoolTeamSnapshot {
  return {
    ...SAMPLE_SCHOOL_TEAM_SNAPSHOT,
    schoolId,
    schoolName,
    semesterLabel: SAMPLE_SCHOOL_TEAM_SNAPSHOT.semesterLabel,
  };
}

/**
 * Sample district list — superintendent view. When real data is populated, this
 * is replaced with a query against `schools` joined to the latest snapshot
 * counts. Helper counts and focus summaries are intentionally light here.
 */
export interface DistrictSchoolSummary {
  schoolId: string;
  schoolName: string;
  semesterLabel: string;
  helperCount: number;
  /** A one-line summary surfaced from the latest observation (UI shows muted). */
  headlineNote?: string;
  /** Focus cards at or above the 25% privacy threshold. */
  focusAreaCount: number;
  /** True when helpers exist and at least one focus area is visible. */
  hasAggregateSignal: boolean;
}

export const SAMPLE_DISTRICT_NAME = "Riverside Unified";

export const SAMPLE_DISTRICT_SCHOOLS: DistrictSchoolSummary[] = [
  {
    schoolId: "sample-lincoln-elementary",
    schoolName: "Lincoln Elementary",
    semesterLabel: "Spring 2026",
    helperCount: 47,
    focusAreaCount: 4,
    hasAggregateSignal: true,
    headlineNote: "Staff are returning after difficult weeks at higher rates than last semester.",
  },
  {
    schoolId: "sample-rosewood-middle",
    schoolName: "Rosewood Middle School",
    semesterLabel: "Spring 2026",
    helperCount: 62,
    focusAreaCount: 4,
    hasAggregateSignal: true,
    headlineNote: "Conversation Lab usage with parents is the dominant focus area this semester.",
  },
  {
    schoolId: "sample-mariposa-high",
    schoolName: "Mariposa High School",
    semesterLabel: "Spring 2026",
    helperCount: 88,
    focusAreaCount: 4,
    hasAggregateSignal: true,
    headlineNote: "Self-regulation work is climbing alongside scenario practice for colleagues.",
  },
];
